// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import type { StepComponentProps } from '../../types/wizard';
import { FileUploadField } from '../shared/FileUploadField';

interface StepOtherChargesDetailsProps extends StepComponentProps {
  policyTypeCategory?: string | null;
}

export function StepOtherChargesDetails({
  data,
  onFieldChange,
  pendingFiles,
  onAddFile,
  onRemoveFile,
  policyTypeCategory,
}: StepOtherChargesDetailsProps) {
  const inputClass =
    "w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6]";

  const textareaClass =
    "w-full min-h-[120px] px-md py-[12px] rounded-lg border border-grey-04 bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary resize-y placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal";

  const labelClass = "text-black font-sans text-body-02 font-medium leading-normal not-italic";

  const isInvestment = policyTypeCategory?.toUpperCase() === 'INVESTMENT';

  // ========================================================================
  // Investment flow
  // ========================================================================
  if (isInvestment) {
    return (
      <div className="flex flex-col gap-lg">
        <h2 className="text-black font-sans text-h5 font-normal leading-normal not-italic">
          Other Charges &amp; Details
        </h2>

        {/* Row 1: Guaranteed Returns Details | Terminal Bonus Details */}
        <div className="flex flex-row gap-lg">
          <div className="flex flex-col gap-sm flex-1">
            <div className={labelClass}>
              Guaranteed Returns Details <span className="text-[#E7000B]">*</span>
            </div>
            <input
              type="text"
              value={(data.guaranteedReturnsDetails as string) || ''}
              onChange={(e) => onFieldChange('guaranteedReturnsDetails', e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="flex flex-col gap-sm flex-1">
            <div className={labelClass}>
              Terminal Bonus Details <span className="text-[#E7000B]">*</span>
            </div>
            <input
              type="text"
              value={(data.terminalBonusDetails as string) || ''}
              onChange={(e) => onFieldChange('terminalBonusDetails', e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        {/* Row 2: Other Charge Details */}
        <div className="flex flex-col gap-sm">
          <div className={labelClass}>
            Other Charge Details <span className="text-[#E7000B]">*</span>
          </div>
          <textarea
            placeholder="Other Charge Details"
            maxLength={2000}
            value={(data.otherChargeDetails as string) || ''}
            onChange={(e) => onFieldChange('otherChargeDetails', e.target.value)}
            className={textareaClass}
          />
        </div>
      </div>
    );
  }

  // ========================================================================
  // Pension flow (existing layout)
  // ========================================================================
  return (
    <div className="flex flex-col gap-lg">
      <h2 className="text-black font-sans text-h5 font-normal leading-normal not-italic">
        Other charges and details
      </h2>

      {/* Platform Fees */}
      <div className="flex flex-col gap-sm">
        <div className={labelClass}>
          Platform fees
        </div>
        <input
          type="text"
          placeholder="Platform fees details"
          value={(data.platformFees as string) || ''}
          onChange={(e) => onFieldChange('platformFees', e.target.value)}
          className={inputClass}
        />
      </div>

      {/* Custodian Fees */}
      <div className="flex flex-col gap-sm">
        <div className={labelClass}>
          Custodian fees
        </div>
        <input
          type="text"
          placeholder="Custodian fees details"
          value={(data.custodianFees as string) || ''}
          onChange={(e) => onFieldChange('custodianFees', e.target.value)}
          className={inputClass}
        />
      </div>

      {/* Transaction Costs */}
      <div className="flex flex-col gap-sm">
        <div className={labelClass}>
          Transaction costs
        </div>
        <input
          type="text"
          placeholder="Transaction costs details"
          value={(data.transactionCosts as string) || ''}
          onChange={(e) => onFieldChange('transactionCosts', e.target.value)}
          className={inputClass}
        />
      </div>

      {/* Other Details */}
      <div className="flex flex-col gap-sm">
        <div className={labelClass}>
          Other details
        </div>
        <textarea
          placeholder="Any other charges or details..."
          maxLength={2000}
          value={(data.otherDetails as string) || ''}
          onChange={(e) => onFieldChange('otherDetails', e.target.value)}
          className={textareaClass}
        />
      </div>

      {/* Upload Documents */}
      <FileUploadField
        fieldKey="otherChargesDocUpload"
        files={pendingFiles?.get('otherChargesDocUpload')}
        onAddFile={onAddFile}
        onRemoveFile={onRemoveFile}
      />
    </div>
  );
}
